/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { PageSection } from 'adc-ui-components';
import { withFormik } from 'formik';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash.get';

import Card from '../../../components/Card';
import SwapOptionGroup from '../../../components/SwapOptionGroup';

import { getDeviceFromAccount } from '../../../helpers/account';
import { getEligibleDevices } from '../../../helpers/swaps';
import { sendPageloadEvent } from '../../../actions/adobe';
import useClientSize from '../../../hooks/useClientSize';
import { joinClass } from '../../../helpers/component';
import useViewport from '../../../hooks/useViewport';

const Form = ({
  handleSubmit,
  values: { devices },
  requiredDevices,
  optionalDevices,
  handleSendPageloadEvent,
  eligibleDevices,
  ownedDevices,
  hasNoLeasedDevice,
  sikEligible,
  match,
}) => {
  const viewport = useViewport();
  const polaris = useClientSize('.page-footer', () => {
    document.body.classList.remove('cmp-hide');
  });
  const mobile = viewport.width <= 768 || viewport.height <= 600;
  const attachToFooter = viewport.height - polaris.top > 0;
  const fixedActionClass = (() => {
    if (mobile) return '';
    return attachToFooter ? 'fixed-action--attached' : 'fixed-action--fixed';
  })();

  // code to run on component mount
  useEffect(() => {
    const component = eligibleDevices.map((device) => {
      const { lob, model } = device;
      const program = get(device, 'programs[0].name') || 'N/A';
      const eligibility = device.eligibility === 'MANDATORY' ? 'required' : 'optional';

      return {
        placementName: 'myaccount|devices|swaps',
        componentName: `${lob},${model},${program},${eligibility}`,
      };
    });
    handleSendPageloadEvent(match, {
      deleteComponent: true,
      component,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageSection>
      <form
        onSubmit={handleSubmit}
        noValidate
      >
        <h1 className="visuallyhidden">Equipment Swap</h1>

        {!!requiredDevices.length && (
          <>
            <div className="hgroup">
              <h2>Required update</h2>
              <p>
                We need to swap out some of your equipment to make sure you get the best service
                possible. There will be no additional costs or changes to your monthly bill.
              </p>
            </div>

            <SwapOptionGroup
              className={(optionalDevices.length || ownedDevices.length) && 'mb18'}
              devices={requiredDevices}
            />
          </>
        )}

        {!!optionalDevices.length && (
          <>
            <div className="hgroup">
              <h2>Optional updates</h2>
              <p>
                Some of your equipment is eligible for an update, but this is not required.
                There will be no additional costs or changes to your monthly bill.
              </p>
            </div>

            <SwapOptionGroup
              className={ownedDevices.length && 'mb18'}
              devices={optionalDevices}
            />
          </>
        )}

        {!!ownedDevices.length && (
          <>
            <div className="hgroup">
              <h2>Third-party device update</h2>
              <p>
                Your device needs to be updated to support higher speeds.
              </p>
            </div>

            <SwapOptionGroup
              className="mb18"
              devices={ownedDevices}
              isCustomerOwned
              hasNoLeasedDevice={hasNoLeasedDevice}
            />
          </>
        )}

        {!hasNoLeasedDevice && (
          <>
            <Card className="card--warning">
              {`There is no additional cost for updating your equipment and no changes to your monthly bill.
              You will need to return your old equipment within 30 days to avoid being charged.
              Returning is free and easy. `}
              <a
                href="https://www.xfinity.com/support/articles/returning-your-equipment"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More
              </a>
            </Card>

            <div className="fixed-action-spacer">
              <div
                className={joinClass('card-group', 'fixed-action', fixedActionClass)}
              >
                <div className="card-group__item">
                  <div className="card card--action-right-at-768">
                    <div className="fixed-action__wrapper">
                      <div className="card__content">
                        <div className="hgroup">
                          <p className="body1">
                            {devices.length === 0 && 'Select a device to update'}
                            {devices.length > 0 && `${devices.length} device${devices.length > 1 ? 's' : ''} selected to be updated`}
                          </p>
                          {devices.length > 4 && (
                            <>
                              <p className="body2">
                                Updating 5 or more devices will require a professional installation.
                              </p>
                              <p className="body2">
                                {'We\'ll call you to set up an appointment.'}
                              </p>
                            </>
                          )}
                          {devices.length > 0 && devices.length <= 4 && (
                            <p className="body2">
                              {sikEligible
                                ? 'We\'ll send a Self-Install Kit in the mail.'
                                : 'We\'ll call you to set up a professional installation.'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="card__action">
                        <div className="action action--right">
                          <div className="action__item">
                            <button className="button button--primary" type="submit">Continue</button>
                          </div>
                          <div className="action__item">
                            <a className="button" href="/">Cancel</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {!!hasNoLeasedDevice && (
          <div className="text-center">
            <div className="action action--right-centered">
              <div className="action__item">
                <a
                  className="button button--primary"
                  href="https://www.xfinity.com/buy/plan/internet/manage-gateway"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Rent a New Device
                </a>
              </div>
              <div className="action__item">
                <a
                  className="button"
                  href="https://mydeviceinfo.xfinity.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Compatible Equipment
                </a>
              </div>
            </div>
          </div>
        )}
      </form>
    </PageSection>
  );
};

const SwapForm = withFormik({
  mapPropsToValues: ({ requiredDevices }) => ({
    devices: requiredDevices.map(({ serialNumber }) => serialNumber),
  }),
  handleSubmit: (values, { props }) => {
    // get the device objects of the selected devices so we can
    // store in state and use it in the review and confirmation pages
    const optional = props.optionalDevices.filter(({ serialNumber }) => (
      values.devices.indexOf(serialNumber) > -1
    ));
    props.onFormSubmit(props.requiredDevices.concat(optional));
  },
  displayName: 'SwapForm',
})(Form);

const mapStateToProps = ({
  account: { data: accountData },
  swapEligibility: {
    data: swapData,
    data: { sikEligible },
  },
}) => {
  const devices = getDeviceFromAccount(accountData);
  const eligibleDevices = getEligibleDevices(swapData, devices);
  const {
    requiredDevices,
    optionalDevices,
    ownedDevices,
  } = eligibleDevices.reduce((acc, device) => {
    if (device.customerOwned) {
      acc.ownedDevices.push(device);
    } else if (device.eligibility === 'MANDATORY') {
      acc.requiredDevices.push(device);
    } else {
      acc.optionalDevices.push(device);
    }

    return acc;
  }, {
    requiredDevices: [],
    optionalDevices: [],
    ownedDevices: [],
  });

  const hasNoLeasedDevice = !requiredDevices.length
    && !optionalDevices.length
    && ownedDevices.length;

  return {
    sikEligible,
    eligibleDevices,
    requiredDevices,
    optionalDevices,
    hasNoLeasedDevice,
    ownedDevices,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  handleSendPageloadEvent: sendPageloadEvent,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SwapForm);
